/**
 * Link styling utilities for consistent link appearance across the application
 * 
 * Design Standards:
 * - Bill numbers: Normal text color with hover underline (not primary color)
 * - Legislator names: Party-colored with hover underline
 * - General links: Primary color with hover underline (default web convention)
 */

import { getPartyTextColor } from './partyUtils';

/**
 * Get classes for bill number links
 * Bill numbers should be readable in normal text color, with underline on hover to indicate clickability
 * 
 * @param options - Optional configuration
 * @param options.bold - Whether to make the text bold (default: true)
 * @returns Tailwind CSS classes for bill link styling
 * 
 * @example
 * <Link className={getBillLinkClasses()}>HB25-1234</Link>
 * <Link className={getBillLinkClasses({ bold: false })}>HB25-1234</Link>
 */
export const getBillLinkClasses = (options?: { bold?: boolean }): string => {
  const { bold = true } = options || {};
  const fontWeight = bold ? 'font-semibold' : 'font-medium';
  return `text-text-primary dark:text-text-dark-primary ${fontWeight} hover:underline cursor-pointer`;
};

/**
 * Get classes for legislator name links
 * 
 * Guidelines for when to use party colors vs neutral:
 * - Use `neutral: true` when party is shown in a separate column/badge AND the context is data-dense
 *   (e.g., reports, search results, modals where space is limited)
 * - Use party-colored (default) when:
 *   - The name is the only party indicator (e.g., SponsorDisplay, bill sponsor lists)
 *   - The context benefits from visual party identification (e.g., Legislators tab in workspaces)
 * 
 * NOTE: The Legislators tab intentionally uses party-colored names even though a Party column exists,
 * as this provides quick visual identification when scanning the list.
 * 
 * @param party - Party affiliation string (e.g., "Democrat", "Republican", "D", "R")
 * @param options - Optional configuration
 * @param options.bold - Whether to make the text bold (default: false)
 * @param options.neutral - Use neutral color instead of party color (default: false)
 * @returns Tailwind CSS classes for legislator link styling
 * 
 * @example
 * // Party-colored (for Legislators tab, SponsorDisplay, inline mentions)
 * <Link className={getLegislatorLinkClasses(legislator.party)}>John Smith</Link>
 * 
 * // Neutral (for reports, search results where party badge exists)
 * <Link className={getLegislatorLinkClasses(legislator.party, { neutral: true })}>John Smith</Link>
 */
export const getLegislatorLinkClasses = (party?: string, options?: { bold?: boolean; neutral?: boolean }): string => {
  const { bold = false, neutral = false } = options || {};
  const fontWeight = bold ? 'font-semibold' : 'font-medium';
  
  if (neutral) {
    return `text-text-primary dark:text-text-dark-primary ${fontWeight} hover:underline cursor-pointer`;
  }
  
  return `${getPartyTextColor(party)} ${fontWeight} hover:underline cursor-pointer`;
};

/**
 * Get classes for general/action links (default web convention)
 * Use this for navigation links, "View all", "Learn more", etc.
 * 
 * @returns Tailwind CSS classes for general link styling
 * 
 * @example
 * <Link className={getActionLinkClasses()}>View all bills</Link>
 */
export const getActionLinkClasses = (): string => {
  /*
    P3-07: the dark variant was `primary-400`. That is #8B67A2, which measures
    4.07:1 against the #121212 dark surface — under AA for text. It is now
    primary-dark-500 (#B594C8, 7.17:1), which is the value the old
    `dark:hover:` step already used, so dark mode gains contrast without
    changing hue.

    The dark hover step is dropped rather than kept: primary-300 and
    primary-dark-500 are the same hex, so it was a no-op that only looked like
    feedback. `hover:underline` is the feedback, in both modes.
  */
  return 'text-primary-600 dark:text-primary-dark-500 hover:text-primary-700 hover:underline cursor-pointer';
};

